import { FormikErrors, useFormik } from 'formik';
import { useRef } from 'react';

import { ProductGroup } from '../../../types/ProductGroup';
import { isValidEmailAddress } from '../helpers/isValidEmailAddress.helper';
import { translate } from '../helpers/translate.helper';

import { REQUISITION_SELECTABLE_GROUPS } from './requisitionGroups';
import { CompanyEmailResultMessage } from './types/CompanyEmailResultMessage';
import { RequisitionAction } from './types/RequisitionAction';
import { RequisitionGroupSelection } from './types/RequisitionGroupSelection';
import { RequisitionOptions } from './types/RequisitionOptions';
import { RequisitionProductSource } from './types/RequisitionProductSource';
import { RequisitionScope } from './types/RequisitionScope';

interface RequisitionFormValues {
    productSource: RequisitionProductSource;
    groupSelection: RequisitionGroupSelection;
    groups: ProductGroup[];
    emailAddress: string;
}

export interface VesselRequisitionFormProps {
    scope: RequisitionScope;
    vesselEmailAddress?: string;
    onDismiss: () => void;
    onSubmit: (
        options: RequisitionOptions,
        action: RequisitionAction,
        emailAddress?: string
    ) => Promise<void | CompanyEmailResultMessage>;
}

export const useVesselRequisitionForm = ({
    scope,
    vesselEmailAddress,
    onDismiss,
    onSubmit,
}: VesselRequisitionFormProps) => {
    const actionRef = useRef(RequisitionAction.DOWNLOAD);
    const formik = useFormik<RequisitionFormValues>({
        initialValues: {
            productSource: RequisitionProductSource.CATALOGUE,
            groupSelection: RequisitionGroupSelection.ALL,
            groups: REQUISITION_SELECTABLE_GROUPS,
            emailAddress: vesselEmailAddress ?? '',
        },
        validate: (values) => {
            const errors: FormikErrors<RequisitionFormValues> = {};
            if (values.groups.length === 0) {
                errors.groups = translate('requisition.customize.selectGroup');
            }
            if (
                scope === RequisitionScope.VESSEL &&
                !vesselEmailAddress?.trim() &&
                actionRef.current === RequisitionAction.SEND &&
                !isValidEmailAddress(values.emailAddress.trim())
            ) {
                errors.emailAddress = translate('vessel.edit.validEmailAddress');
            }
            return errors;
        },
        onSubmit: async (values, { setStatus }) => {
            setStatus(undefined);
            const recipient = values.emailAddress.trim();
            const invalidEmail = !isValidEmailAddress(recipient);
            try {
                const result = await onSubmit(
                    {
                        productSource: values.productSource,
                        productGroups:
                            values.groupSelection === RequisitionGroupSelection.ALL ? undefined : values.groups,
                    },
                    actionRef.current,
                    scope === RequisitionScope.COMPANY || vesselEmailAddress?.trim() || invalidEmail
                        ? undefined
                        : recipient
                );
                if (result) {
                    if (result.queuedCount === 0) {
                        setStatus(
                            translate('requisition.customize.noRecipients', {
                                skippedCount: String(result.skippedCount),
                            })
                        );
                        return;
                    }
                    setStatus(
                        translate('requisition.customize.companyQueued', {
                            queuedCount: String(result.queuedCount),
                            skippedCount: String(result.skippedCount),
                        })
                    );
                    return;
                }
                onDismiss();
            } catch {
                setStatus(translate('requisition.customize.error'));
            }
        },
    });
    const submit = async (action: RequisitionAction) => {
        if (formik.isSubmitting) {
            return;
        }
        actionRef.current = action;
        await formik.submitForm();
    };

    return { formik, submit, action: actionRef.current };
};
